import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { map } from 'rxjs';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { RESTService } from './rest.service';
import { StorageService } from './storage.service';

@Injectable({
    providedIn: 'root'
})

/** The JWTService class handles the management of JSON Web Tokens, including setting and refreshing
tokens, checking expiration, and logging out. */
export class JWTService {

    private refreshTokenTimeout: any;
    private checkRefreshExpirationTimeout: any;
    private expires: Date;
    private timeout: number;
    private timeBeforeLogout: number = 600 * 1000; // in seconds

    constructor(
        private _rest: RESTService,
        private _storage: StorageService,
        private _router: Router,
        private _dialogService: DialogService
    ) { }


   /**
    * This function sets a token and starts a refresh token timer.
    * @param {string} token - a string representing the authentication token that will be stored in the
    * application's storage.
    */
    setToken(token: string) {
        this._storage.set('token', token);
        this.startRefreshTokenTimer();
    }

    /**
     * This function retrieves a token from storage or returns an empty string if it doesn't exist.
     * @returns If the token exists in the storage, it will be returned. Otherwise, an empty string
     * will be returned.
     */
    private getToken() {
        const token = this._storage.get('token');
        if(token !== null) {
            return token;
        } else {
            return '';
        }
    }

    /**
     * The function logs out the user by removing their login status, user data, and token from storage
     * and navigating to the login page.
     */
    private logout() {
        this._storage.unset('loginStatus');
        this._storage.unset('user');
        this._storage.unset('token');
        this._router.navigate(['login']);
    }

    /**
     * The function checks if the refresh token has expired and logs out the user if it has.
     */
    public checkRefreshExpiration() {
        // console.log('checkRefreshExpiration');

        let jwtToken = JSON.parse(atob(this.getToken()?.split('.')[1]));
        let expires = new Date(jwtToken.exp * 1000); // get expired date time value
        if(expires.getTime() < Date.now()) {
            this.stopRefreshTokenTimer();
            this.stopRefreshExpirationTimer();
            this._dialogService.dialog.closeAll();
            this.logout();
        }
        // console.log(expires);
        return;
    }

    /**
     * This function refreshes the authentication token and prompts the user to stay logged in or log
     * out.
     */
    private refreshToken() {
        // console.log('Refreshing token');
        this.checkRefreshExpirationTimeout = setTimeout(() => this.checkRefreshExpiration(), this.timeBeforeLogout);
        this._dialogService.openDialog('Achtung', 'Ihre Loginsitzung läuft ab. Möchten Sie eingeloggt bleiben?');
        this._dialogService.onSave(() => {
            if(this.expires.getTime() > Date.now()) {
                return this._rest.get('refreshToken').pipe(map(async (result) => {
                    this.stopRefreshExpirationTimer();
                    this.setToken(result.data.jwt);
                })).subscribe();
            }
            return;
        });
        this._dialogService.onCancel(() => {
            this.stopRefreshTokenTimer();
            this.stopRefreshExpirationTimer();
            this.logout();
        });
    }

    /**
     * The function sets a timeout to refresh a JWT token 10 minutes before it expires.
     */
    private startRefreshTokenTimer(): void {
        // parse json object from base64 encoded jwt token
        const jwtToken = JSON.parse(atob(this.getToken()?.split('.')[1]));

        if(!jwtToken) {
            this.stopRefreshTokenTimer();
            this.logout();
        }

        // set a timeout to refresh the token 10 minutes before it expires
        this.expires = new Date(jwtToken.exp * 1000); // get expired date time value
        //console.log('expires: ' + this.expires);
        this.timeout = this.expires.getTime() - Date.now() - (this.timeBeforeLogout); // add 10 minutes
        //console.log('timeout: ' + this.timeout);
        this.refreshTokenTimeout = setTimeout(() => this.refreshToken(), this.timeout);
    }

    /**
     * Stop the refreshTokenTimer
     */
    private stopRefreshTokenTimer() {
        clearTimeout(this.refreshTokenTimeout);
    }

    /**
     * Stop the stopRefreshExpirationTimer
     */
    private stopRefreshExpirationTimer() {
        clearTimeout(this.checkRefreshExpirationTimeout);
    }
}
